import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { connect, useSelector } from 'react-redux'
import { Link as RLink, useNavigate } from 'react-router-dom'
import TablePagination from './TablePagination.jsx'
import { Link, styled, Tag, LinkStyle, Waves, spacing } from '@pergas-common/pergas-components'
import { Table, Td } from '../../pergas-components/index.js'
import { Add, Favorite, FavoriteFilled, ArrowDown, ArrowRight } from '@pergas-common/pergas-icons'
import PageToolbar from './PageToolbar.js'
import storage from '../../redux/storage.js'
import redux from '../../redux/index.js'
import SearchField from '../SearchField.js'
import FilterFavoriteButton from '../filter/FilterFavoriteButton'
import DeleteItem from '../dialogs/DeleteItem.js'
import EditDepartment from '../dialogs/EditDepartment'
import { DeleteItemIcon, TemplateManagerIcon, CategoryIcon, DepartmentIcon } from '../icons.js'
import { createDateAccessor, SharePointSite, TeamsToolIcon } from './columns.js'
import { responsiblePerson, sortArray } from '../../util.js'
import { DEFAULT_OBJECT_TYPE_COLOR } from '../style.js'
import Tooltip from '../Tooltip.js'
import { SubRow, SubRowContent } from '../SubRow.js'
import { selectLocale } from '../../redux/locale/selectors.js'
import useRequest from '../../hooks/useRequest.js'
import api from '../../redux/api.js'
import FileView from '../FileView.jsx'
import { makeSelectorByTemplatesEnabled } from '../../redux/login/selectors'
import { makeSelectorDepartment, makeSelectorSelectedItemId } from '../../redux/department/selectors'
import ColumnMultiSelectFilter from '../filter/ColumnMultiSelectFilter.js'
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  Popover,
  Radio,
  RadioGroup,
  TextField
} from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'

const PageHolder = styled.div`
  display: flex;
  overflow: hidden;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`

const TableHolder = styled.div`
  display: flex;
  overflow-y: scroll;
  flex-grow: 1;
  flex-basis: 50px;
  min-height: 0;
`

const NameHolder = styled.div`
  display: inline-block;
  margin: 0 ${spacing(1)};
`

const TagHolder = styled.span`
  margin-right: ${spacing(1)};
`

const Spacer = styled.div`
  display: inline-block;
  margin-right: 2px;
`

const ExpandedArrows = styled.div`
  display: inline-block;
  padding: ${spacing(1)};
`

const InternalLink = styled(RLink)`
  ${LinkStyle}
`
const Label = styled.div`
  ${({ theme }) => theme.typography.body};
  padding: ${spacing(0.5)} ${spacing(1)};
`

const FavoriteHolder = styled.div`
  margin-top: -2px;
`

const TAG_FILTER_KEY = 'tags_string'
const TAG_FILTER_OP = '~'
const TAG_FILTER_MODE_OR = 'or'
const TAG_FILTER_MODE_AND = 'and'
const OBJECT_TYPE_FILTER_KEY = 'object_type_name_string'

const TagsHeaderHolder = styled.div`
  display: inline-flex;
  align-items: center;
`

const TagFilterBody = styled.div`
  width: 280px;
  padding: 8px;
`

const TagFilterActions = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 8px;
`

const AsyncCategoryDropdown = ({
  row,
  selectedCategory,
  setSelectedCategory,
  setSelectedDepartmentId,
  toggleAllPageRowsSelected,
  getRootFolder,
  resetFileSearch
}) => {
  const { original } = row
  const { id } = original
  const locale = useSelector(selectLocale)
  const [request, data, status] = useRequest(api.getCategories, 'category')
  useEffect(() => {
    request({ query: [{ key: 'collection_id', op: '=', value: original.id }], sort: 'name.asc' })
  }, [])
  const isLoading = status === 'pending'

  if (data === null || data?.length === 0) {
    return (
      <SubRow
        key={id} subRowChildren={[{
          element: (
            <SubRowContent flex={2}>
              <Label>{locale.missing_content_page}</Label>
            </SubRowContent>
          ),
          key: 'empty'
        }]} colSpan={6} isFirst isLast
      />
    )
  }

  if (isLoading) {
    return (
      <SubRow
        key={id} subRowChildren={[{
          element: (
            <SubRowContent flex={2}>
              <Spacer>
                <Spacer>
                  <Spacer>
                    <Waves width={24} height={24} />
                  </Spacer>
                </Spacer>
              </Spacer>
            </SubRowContent>
          ),
          key: 'loading'
        }]} colSpan={6} isFirst isLast
      />
    )
  }

  const onRowClick = (category) => {
    getRootFolder(category)
    setSelectedDepartmentId(0)
    setSelectedCategory(category)
    resetFileSearch('category')
    toggleAllPageRowsSelected(false)
  }

  const renderRows = data?.map((category, index) => {
    const { id, name, person_role: personRole } = category
    const isFirst = index === 0
    const isLast = index === data.length - 1

    const subRowChildren = [{
      element: (
        <SubRowContent flex={1}>
          <CategoryIcon width={18} height={18} />
          <Label><InternalLink to={`/categories/edit?id=${id}`}>{name}</InternalLink></Label>
        </SubRowContent>
      ),
      key: `${id}_name`
    },
    {
      element: (
        <SubRowContent flex={2}>
          {responsiblePerson(personRole)?.person_name}
        </SubRowContent>
      ),
      key: `${id}_responsible`
    }
    ]

    const isSelected = selectedCategory?.id === category.id
    return (
      <SubRow key={id} isSelected={isSelected} onClick={() => onRowClick(category)} subRowChildren={subRowChildren} colSpan={5} isFirst={isFirst} isLast={isLast} />
    )
  })
  return renderRows
}

const DepartmentPage = ({
  locale,
  limit,
  offset,
  orderBy,
  order,
  search,
  filterQueries,
  resetSearch,
  setFilterQueries,
  getPageItems,
  setSelectedDepartmentId,
  setOrder,
  setLimit,
  setOffset,
  setSearch,
  fileSearchState,
  canUpdate,
  canDelete,
  deleteItem,
  onDeleteItemClick,
  onDeleteOk,
  onDeleteCancel,
  editItem,
  onEditItemClick,
  onEditOk,
  onEditCancel,
  canCreate,
  onShowAddDialog,
  onFavoriteItemClick,
  sharePointSaveEnabled,
  getRootFolder,
  resetFileSearch,
  branding,
  tmUrl
}) => {
  const navigate = useNavigate()
  const settings = storage.getPageSettings('department')

  const [showFileView, setShowFileView] = useState(settings.showFileView || false)
  function onToggleFileView () {
    storage.putPageSetting('department', { showFileView: !showFileView })
    setShowFileView(!showFileView)
  }

  function onSetFileViewHeight (height) {
    storage.putPageSetting('department', { fileViewHeight: height })
  }

  const selectItems = useMemo(makeSelectorDepartment, [])
  const items = useSelector((state) => selectItems(state))

  const [tagFilterAnchorEl, setTagFilterAnchorEl] = useState(null)
  const [tagFilterSearch, setTagFilterSearch] = useState('')
  const [tagDraftSelection, setTagDraftSelection] = useState([])
  const [tagMatchMode, setTagMatchMode] = useState(TAG_FILTER_MODE_OR)

  const selectedTagMatchMode = useMemo(() => {
    const modeFromFilters = (filterQueries || [])
      .find((q) => q.key === TAG_FILTER_KEY && q.op === TAG_FILTER_OP)?.matchMode

    return modeFromFilters === TAG_FILTER_MODE_AND
      ? TAG_FILTER_MODE_AND
      : TAG_FILTER_MODE_OR
  }, [filterQueries])

  const selectedTags = useMemo(() => {
    return (filterQueries || [])
      .filter((q) => q.key === TAG_FILTER_KEY && q.op === TAG_FILTER_OP)
      .map((q) => q.value)
      .filter((value) => typeof value === 'string' && value.length > 0)
  }, [filterQueries])

  const selectedObjectTypeValues = useMemo(() => {
    return (filterQueries || [])
      .filter((q) => q.key === OBJECT_TYPE_FILTER_KEY && q.op === TAG_FILTER_OP)
      .map((q) => q.value)
      .filter((value) => typeof value === 'string' && value.length > 0)
  }, [filterQueries])

  const tagOptions = useMemo(() => {
    const unique = new Set()
    ;(items || []).forEach((item) => {
      ;(item?.tags || []).forEach((tag) => {
        const value = tag?.name
        if (typeof value === 'string' && value.trim() !== '') {
          unique.add(value.trim())
        }
      })
    })
    return Array.from(unique).sort((a, b) => a.localeCompare(b, 'sv'))
  }, [items])

  const objectTypeOptions = useMemo(() => {
    const unique = new Set()
    ;(items || []).forEach((item) => {
      const value = item?.object_type_name
      if (typeof value === 'string' && value.trim() !== '') {
        unique.add(value.trim())
      }
    })
    return Array.from(unique).sort((a, b) => a.localeCompare(b, 'sv'))
  }, [items])

  const filteredTagOptions = useMemo(() => {
    const query = tagFilterSearch.trim().toLowerCase()
    if (query === '') {
      return tagOptions
    }
    return tagOptions.filter((tag) => tag.toLowerCase().includes(query))
  }, [tagFilterSearch, tagOptions])

  const isTagFilterOpen = Boolean(tagFilterAnchorEl)

  const openTagFilter = useCallback((event) => {
    event.preventDefault()
    event.stopPropagation()
    setTagDraftSelection(selectedTags)
    setTagMatchMode(selectedTagMatchMode)
    setTagFilterSearch('')
    setTagFilterAnchorEl(event.currentTarget)
  }, [selectedTagMatchMode, selectedTags])

  const closeTagFilter = useCallback(() => {
    setTagFilterAnchorEl(null)
  }, [])

  const applyTagFilter = useCallback(() => {
    const retained = (filterQueries || []).filter((q) => q.key !== TAG_FILTER_KEY)
    const tagQueries = tagDraftSelection
      .filter((tag) => typeof tag === 'string' && tag.length > 0)
      .map((tag) => ({
        key: TAG_FILTER_KEY,
        value: tag,
        op: TAG_FILTER_OP,
        matchMode: tagMatchMode
      }))
    setFilterQueries([...retained, ...tagQueries])
    closeTagFilter()
  }, [closeTagFilter, filterQueries, setFilterQueries, tagDraftSelection, tagMatchMode])

  const applyObjectTypeFilter = useCallback((values) => {
    const retained = (filterQueries || []).filter((q) => q.key !== OBJECT_TYPE_FILTER_KEY)
    const valueQueries = (values || [])
      .filter((value) => typeof value === 'string' && value.length > 0)
      .map((value) => ({ key: OBJECT_TYPE_FILTER_KEY, value, op: TAG_FILTER_OP }))
    setFilterQueries([...retained, ...valueQueries])
  }, [filterQueries, setFilterQueries])

  const toggleTagDraftValue = useCallback((tag) => {
    setTagDraftSelection((prev) => {
      if (prev.includes(tag)) {
        return prev.filter((value) => value !== tag)
      }
      return [...prev, tag]
    })
  }, [])

  const selectSelectedDepartmentId = useMemo(makeSelectorSelectedItemId, [])
  const selectedDepartmentId = useSelector((state) => selectSelectedDepartmentId(state))

  const selectorByTemplatesEnabled = useMemo(makeSelectorByTemplatesEnabled, [])
  const templatesEnabled = useSelector((state) => selectorByTemplatesEnabled(state))

  useEffect(() => {
    getPageItems()
  }, [getPageItems, limit, offset, orderBy, order, search, filterQueries])

  const [selectedCategory, setSelectedCategory] = useState(null)
  const renderRowSubComponent = useCallback(
    ({ row, toggleAllPageRowsSelected }) => (
      <AsyncCategoryDropdown
        row={row}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        setSelectedDepartmentId={setSelectedDepartmentId}
        toggleAllPageRowsSelected={toggleAllPageRowsSelected}
        getRootFolder={getRootFolder}
        resetFileSearch={resetFileSearch}
      />
    ),
    [selectedCategory, setSelectedCategory, setSelectedDepartmentId, getRootFolder, resetFileSearch]
  )

  const setPageSizeRef = useRef(() => {})

  const manualSort = useCallback(({ id, isSorted, isSortedDesc }) => {
    if (isSorted && !isSortedDesc) {
      setOrder(id, 'desc')
    } else if (isSorted && isSortedDesc) {
      setOrder('', '')
    } else {
      setOrder(id, 'asc')
    }
  }, [setOrder])

  const selectedRowIds = useMemo(() => {
    const index = items.findIndex(item => item.id === selectedDepartmentId)
    if (index !== -1) {
      return { [String(index)]: true }
    } else {
      return {}
    }
  }, [items, selectedDepartmentId])

  const onExpandArrow = (row) => {
    return (e) => {
      e.stopPropagation()
      row.toggleRowExpanded()
    }
  }

  const columns = useMemo(() => {
    return [
      {
        Header: locale.name,
        Cell: ({ cell }) => {
          const { row } = cell
          const { isExpanded, original } = row
          const Arrow = isExpanded ? ArrowDown : ArrowRight
          return (
            <Td
              {...cell.getCellProps()} left={
                <>
                  <ExpandedArrows>
                    <Arrow width={12} height={12} style={{ cursor: 'pointer' }} onClick={onExpandArrow(row)} />
                  </ExpandedArrows>
                  <DepartmentIcon style={{ width: 16, height: 16 }} />
                  <NameHolder>
                    {!!original.name && (canUpdate ? <InternalLink to={`/departments/edit?id=${original.id}`}>{original.name}</InternalLink> : <span>{original.name}</span>)}
                  </NameHolder>

                </>
            }
            />
          )
        },
        canSort: true,
        id: 'name',
        size: 'md',
        manualSort,
        sortType: () => {}
      },
      {
        id: 'object_type_name',
        Header: (
          <ColumnMultiSelectFilter
            title={locale.department_object_type}
            options={objectTypeOptions}
            selectedValues={selectedObjectTypeValues}
            onApply={applyObjectTypeFilter}
            locale={locale}
          />
        ),
        Cell: ({ cell }) => {
          const { row: { original } } = cell
          return <Td {...cell.getCellProps()} center={original.object_type_name && <Tag color={original.object_type_color || DEFAULT_OBJECT_TYPE_COLOR} textColor={original.object_type_color ? '#FFFFFF' : '#3a4a54'} border='#969696'>{original.object_type_name}</Tag>} />
        },
        size: 'sm',
        canSort: true,
        sortType: () => {},
        manualSort
      },
      {
        id: 'tags_string',
        Header: (
          <TagsHeaderHolder>
            <span>{locale.tags}</span>
            <IconButton
              size='small'
              onClick={openTagFilter}
              title={locale.filter || locale.tags}
            >
              <FilterListIcon
                fontSize='inherit'
                style={{ color: selectedTags.length > 0 ? '#64B5F6' : '#FFFFFF' }}
              />
            </IconButton>
          </TagsHeaderHolder>
        ),
        Cell: ({ cell }) => {
          const { row: { original } } = cell
          const tags = original.tags && original.tags.length > 0 && sortArray(original.tags, 'name').slice(0, 2).map(({ color, id, name }) => {
            return <TagHolder key={id}><Tag textColor='#FFFFFF' color={color}>{name}</Tag></TagHolder>
          })
          return (<Td {...cell.getCellProps()} left={tags && tags} />)
        },
        canSort: true,
        sortType: () => {},
        manualSort
      },
      {
        id: 'updated_at',
        Header: locale.updated_at,
        manualSort,
        accessor: createDateAccessor('updated_at'),
        sortType: () => {}
      },
      {
        id: 'toolbar',
        Header: locale.tool_belt,
        Cell: ({ cell }) => {
          const { row: { original } } = cell
          return (
            <Td
              {...cell.getCellProps()}
              left={
                <>
                  {sharePointSaveEnabled && <Spacer>{SharePointSite(original)}</Spacer>}
                  {sharePointSaveEnabled && <TeamsToolIcon row={original} />}
                  {templatesEnabled && <Tooltip content={locale.template_manager}><Link href={`${tmUrl}?pds_id=${original.id}&pds_type=department`}> <TemplateManagerIcon width={18} height={18} /></Link></Tooltip>}
                  <Tooltip content={locale.favorite}><Spacer><Link onClickHandler={() => { onFavoriteItemClick(original) }}><FavoriteHolder>{original.is_favorite ? <FavoriteFilled color='#ca6eb6' width={18} height={18} /> : <Favorite color='#ca6eb6' width={18} height={18} />}</FavoriteHolder></Link></Spacer></Tooltip>
                </>
              }
              right={canDelete && <Tooltip side='left' content={locale.delete}><Link onClickHandler={() => { onDeleteItemClick(original) }}><DeleteItemIcon /></Link></Tooltip>}
            />
          )
        },
        size: 'sm',
        isSortable: false
      }
    ]
  }, [locale, canUpdate, canDelete, sharePointSaveEnabled, manualSort, onDeleteItemClick, onEditItemClick, onFavoriteItemClick, openTagFilter, selectedTags, objectTypeOptions, selectedObjectTypeValues, applyObjectTypeFilter])

  let initialSortBy = []
  if (orderBy) {
    initialSortBy = [{
      id: orderBy,
      desc: order === 'desc'
    }]
  }

  let selectedSiteItem = items.find(i => i.id === selectedDepartmentId)
  let searchState

  if (selectedSiteItem) {
    searchState = fileSearchState.department
  } else {
    selectedSiteItem = selectedCategory
    searchState = fileSearchState.category
  }

  const pagination = (
    <TablePagination
      limit={limit}
      offset={offset}
      onRowsPerPageChange={(e) => {
        setLimit(e.target.value)
        setPageSizeRef.current(e.target.value)
      }}
      onPageChange={(e, number) => setOffset(number * limit)}
    />
  )

  return (
    <PageHolder>
      {editItem && <EditDepartment isEditing item={editItem} onOk={onEditOk} onCancel={onEditCancel} />}
      {deleteItem && <DeleteItem text={deleteItem.name} onOk={() => { onDeleteOk(deleteItem) }} onCancel={onDeleteCancel} />}
      <PageToolbar
        left={
          <>
            <DepartmentIcon style={{ width: 20, height: 20 }} />
            <span>{locale.departments}</span>
            {canCreate && (
              <Link onClickHandler={() => {
                navigate('/departments/new')
              }}
              >
                <Add width={20} height={20} color='#447fb1' />
              </Link>
            )}
            <FilterFavoriteButton filterQueries={filterQueries} onFilterChange={setFilterQueries} />
          </>
        }
        center={<SearchField resetSearch={resetSearch} onChange={setSearch} value={search} />}
      />
      <TableHolder>
        <Table
          columnResizeStorageKey='department'
          columns={columns}
          data={items}
          initialPageSize={limit}
          initialSortBy={initialSortBy}
          renderRowSubComponent={renderRowSubComponent}
          selectedRowIds={selectedRowIds}
          onRowClick={(e, row, toggleAllPageRowsSelected) => {
            getRootFolder(row.original)
            setSelectedCategory(null)
            setSelectedDepartmentId(row.original.id)
            toggleAllPageRowsSelected(false)
            row.toggleRowSelected(true)
          }}
        >
          {({ setPageSize }) => {
            // Store table callback in a ref so we can call it from elsewhere
            setPageSizeRef.current = setPageSize
          }}
        </Table>
      </TableHolder>
      <Popover
        open={isTagFilterOpen}
        anchorEl={tagFilterAnchorEl}
        onClose={closeTagFilter}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableEnforceFocus
        disableAutoFocus
      >
        <TagFilterBody onClick={(e) => e.stopPropagation()}>
          <TextField
            fullWidth
            size='small'
            label={locale.search}
            value={tagFilterSearch}
            onChange={(e) => setTagFilterSearch(e.target.value)}
          />
          <FormLabel component='legend' style={{ marginTop: 8 }}>{locale.match_mode || 'Match mode'}</FormLabel>
          <RadioGroup row value={tagMatchMode} onChange={(e) => setTagMatchMode(e.target.value)}>
            <FormControlLabel value={TAG_FILTER_MODE_OR} control={<Radio size='small' />} label={locale.any || 'Any'} />
            <FormControlLabel value={TAG_FILTER_MODE_AND} control={<Radio size='small' />} label={locale.all || 'All'} />
          </RadioGroup>
          <FormGroup style={{ maxHeight: 240, overflowY: 'auto' }}>
            {filteredTagOptions.map((tag) => (
              <FormControlLabel
                key={tag}
                control={<Checkbox size='small' checked={tagDraftSelection.includes(tag)} onChange={() => toggleTagDraftValue(tag)} />}
                label={tag}
              />
            ))}
          </FormGroup>
          <TagFilterActions>
            <Button size='small' onClick={() => setTagDraftSelection([])}>{locale.clear || 'Clear'}</Button>
            <Button size='small' variant='contained' onClick={applyTagFilter}>{locale.apply || 'Apply'}</Button>
          </TagFilterActions>
        </TagFilterBody>
      </Popover>
      <FileView siteItem={selectedSiteItem} right={pagination} onShowToggle={onToggleFileView} isOpen={showFileView} initialHeight={settings.fileViewHeight} onSetHeight={onSetFileViewHeight} searchState={searchState} />
    </PageHolder>
  )
}

const mapStateToProps = (state) => {
  const { locale, department, files, login } = state
  const userData = login.userData || {}
  const permissions = state.login.permissions
  const branding = userData.addinSettings?.branding
  let tmUrl = userData?.tenant_settings?.settings?.tm?.url || userData?.tenant_settings?.tm?.url || userData.tmUrl

  if (!tmUrl) {
    tmUrl = process.env.REACT_APP_TM_URL
  }
  const {
    limit,
    offset,
    orderBy,
    order,
    search,
    filterQueries
  } = department
  return {
    locale: locale.strings,
    limit,
    offset,
    orderBy,
    order,
    search,
    filterQueries,
    canUpdate: permissions.department.canUpdate,
    canDelete: permissions.department.canDelete,
    canCreate: permissions.department.canCreate,
    deleteItem: state.department.deleteItem,
    editItem: state.department.editItem,
    sharePointSaveEnabled: state.login.userData.sharePointSaveEnabled,
    fileSearchState: files.search,
    branding,
    tmUrl
  }
}

const mapDispatchToProps = (dispatch) => {
  const { actions: { department, file } } = redux
  return {
    onShowAddDialog: () => {
      dispatch(department.showAddDepartmentDialog())
    },
    onEditOk: (d) => {
      dispatch(department.hideEditDepartmentDialog())
      dispatch(department.updateDepartment(d))
    },
    onEditItemClick: (d) => {
      dispatch(department.showEditDepartmentDialog(d))
    },
    onEditCancel: () => {
      dispatch(department.hideEditDepartmentDialog())
    },
    onFavoriteItemClick: (d) => {
      dispatch(department.toggleFavorite(d))
    },
    onDeleteItemClick: (d) => {
      dispatch(department.showDeleteDepartmentDialog(d))
    },
    onDeleteOk: (d) => {
      dispatch(department.hideDeleteDepartmentDialog())
      dispatch(department.deleteDepartment(d))
    },
    onDeleteCancel: () => {
      dispatch(department.hideDeleteDepartmentDialog())
    },
    getPageItems: () => {
      dispatch(department.getPageItems())
    },
    setSelectedDepartmentId: (id) => {
      dispatch(department.setSelectedItemId(id))
    },
    setOrder: (orderBy, order) => {
      dispatch(department.setOrder(orderBy, order))
    },
    setLimit: (limit) => {
      dispatch(department.setLimit(limit))
    },
    setOffset: (offset) => {
      dispatch(department.setOffset(offset))
    },
    setSearch: (search) => {
      dispatch(department.setSearch(search))
    },
    resetSearch: () => {
      dispatch(department.resetSearch())
    },
    setFilterQueries: (filterQueries) => {
      dispatch(department.setFilterQueries(filterQueries))
    },
    getRootFolder: (item) => {
      dispatch(file.getRootFolder(item))
    },
    resetFileSearch: (type) => {
      dispatch(file.resetFileSearch(type))
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(DepartmentPage)
